/**
 * What a visitor of each kind reads, by audience code. Mirrors the server's `AUDIENCE_PRESETS`
 * (`app/api/v1/chat.py`: anonymous {1}, lead {1,2}, member {1,2,3}, staff {1,4}) — the Playground
 * "Simulate audience" choices and the Test as guest / signed-in buttons use the same codes.
 *
 * `agent.audiences` ("Who can chat") is an upper limit: what a visitor really reads through an
 * agent is their own audiences ∩ the agent's.
 */
export const GUEST_AUDIENCE = 1;
export const SIGNED_IN_AUDIENCE = 3;
export const STAFF_AUDIENCE = 4;
export const PLAYGROUND_AUDIENCES = [1, 2, 3, 4] as const;

const VIEWER_AUDIENCES: Readonly<Record<number, readonly number[]>> = {
  1: [1],
  2: [1, 2],
  3: [1, 2, 3],
  4: [1, 4],
};

/** Audiences a verified visitor of this kind earns (before the agent's own limit). */
export function viewerAudiences(code: number): readonly number[] {
  return VIEWER_AUDIENCES[code] ?? [GUEST_AUDIENCE];
}

/** Visitor audiences ∩ the agent's limit, sorted. Empty = the agent is closed to this visitor. */
export function effectiveAudiences(viewer: readonly number[], cap: readonly number[]): number[] {
  const allowed = new Set(cap);
  return [...new Set(viewer)].filter((code) => allowed.has(code)).sort((a, b) => a - b);
}

/**
 * The first Playground audience the agent is open to, in the order Guest, Prospect, Member, Staff.
 * A closed agent refuses the message (403), so the Playground starts on a kind it will answer.
 */
export function firstOpenAudience(agentAudiences: readonly number[]): number {
  return PLAYGROUND_AUDIENCES.find((code) => effectiveAudiences(viewerAudiences(code), agentAudiences).length > 0) ?? GUEST_AUDIENCE;
}

/** `?audience=` of the Playground: a code 1–4, anything else is ignored. */
export function parseAudienceParam(raw: string | null): number | null {
  if (raw === null || !/^\d$/.test(raw)) return null;
  const code = Number(raw);
  return (PLAYGROUND_AUDIENCES as readonly number[]).includes(code) ? code : null;
}

/** Playground link for "Test as guest" / "Test as signed-in" (path from `consoleRoutes.playground`). */
export function playgroundLink(playgroundPath: string, audience: number): string {
  return `${playgroundPath}?audience=${String(audience)}`;
}
